import React from 'react';

import dayjs from 'dayjs';

import { formatValue, shortDay } from '../format';
import type { Palette } from '../palette';
import { overrideColor, seriesOverrides } from '../seriesStyle';
import type { Block, Envelope } from '../types';

import Chart, { type ChartOption } from './Chart';

type CalendarProps = { block: Block; envelope: Envelope; palette: Palette; width: number; onPeriod?: (period: string) => void };

export default function Calendar({ block, envelope, palette, width, onPeriod }: CalendarProps) {
  const measure = envelope.columns.find((column) => column.role === 'measure');
  if (!measure) return null;
  const rows = envelope.rows.filter((row) => typeof row.t === 'string' && typeof row[measure.name] === 'number').sort((a, b) => String(a.t).localeCompare(String(b.t)));
  if (!rows.length) return null;
  const values = rows.map((row) => Number(row[measure.name]));
  const override = seriesOverrides(block)[measure.name];
  const color = overrideColor(override, palette, palette.series[0]);
  const option: ChartOption = {
    animation: false,
    textStyle: { fontFamily: palette.sans, color: palette.ink, fontSize: 11 },
    tooltip: {
      confine: true,
      backgroundColor: palette.surface,
      borderColor: palette.hairline,
      textStyle: { color: palette.ink },
      renderMode: 'richText',
      formatter: (point: { value: [string, number] }) => {
        const offset = dayjs(point.value[0]).diff(dayjs(envelope.period.current.from), 'day');
        const lines = [`${shortDay(point.value[0], true)}\n${override?.label ?? measure.label}: ${formatValue(point.value[1], measure.format, { numbers: block.numbers ?? 'full', decimals: measure.decimals })}`];
        for (const window of envelope.period.comparisons) {
          const day = dayjs(window.from).add(offset, 'day').format('YYYY-MM-DD');
          const row = envelope.compare[window.id]?.find((entry) => entry.t === day);
          if (row) lines.push(`${window.label}: ${formatValue(row[measure.name] as number | null, measure.format, { numbers: block.numbers ?? 'full', decimals: measure.decimals })}`);
        }
        return lines.join('\n');
      },
    },
    calendar: {
      range: [String(rows[0].t), String(rows[rows.length - 1].t)],
      orient: width < 600 ? 'vertical' : 'horizontal',
      left: 42,
      right: 16,
      top: 32,
      bottom: 24,
      cellSize: ['auto', 'auto'],
      yearLabel: { show: false },
      monthLabel: { color: palette.ink3 },
      dayLabel: { color: palette.ink3, firstDay: 1 },
      splitLine: { lineStyle: { color: palette.hairline } },
      itemStyle: { color: palette.surface, borderColor: palette.hairline, borderWidth: 1 },
    },
    visualMap: { show: false, min: Math.min(...values), max: Math.max(...values), dimension: 1, inRange: { color: [palette.surface, color] } },
    series: [{ id: measure.name, name: measure.name, type: 'heatmap', coordinateSystem: 'calendar', data: override?.hidden ? [] : rows.map((row) => ({ name: String(row.t), value: [String(row.t), row[measure.name]] })) }],
  };
  return <Chart onItem={block.select !== false && onPeriod ? (day) => onPeriod(`${day}..${day}`) : undefined} option={option} />;
}
